import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { LogOutIcon, BellIcon, UsersIcon, UserPlusIcon, ActivityIcon, StethoscopeIcon, FlaskConicalIcon, RadioIcon, LayoutDashboardIcon, BoxIcon } from 'lucide-react';
export const Layout: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user,
    logout
  } = useAuth();
  const {
    notifications,
    markNotificationRead,
    isOnline
  } = useData();
  const [showNotifications, setShowNotifications] = useState(false);
  if (!user) return null;
  const unreadCount = notifications.filter(n => n.targetRole === user.role && !n.read).length;
  const userNotifications = notifications.filter(n => n.targetRole === user.role);
  const getNavItems = () => {
    switch (user.role) {
      case 'Administrator':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'User Management',
          icon: UsersIcon,
          path: '/users'
        }];
      case 'Reception':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Register Patient',
          icon: UserPlusIcon,
          path: '/register'
        }];
      case 'Nursing':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Observations',
          icon: ActivityIcon,
          path: '/observations'
        }];
      case 'Clinician':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Consultations',
          icon: StethoscopeIcon,
          path: '/consultations'
        }];
      case 'Laboratory':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Investigations',
          icon: FlaskConicalIcon,
          path: '/lab'
        }];
      case 'Radiology':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Scans',
          icon: RadioIcon,
          path: '/radiology'
        }];
      case 'Pharmacy':
        return [{
          name: 'Dashboard',
          icon: LayoutDashboardIcon,
          path: '/'
        }, {
          name: 'Prescriptions',
          icon: BoxIcon,
          path: '/pharmacy'
        }];
      default:
        return [];
    }
  };
  return <div className="flex h-screen bg-slate-50" data-id="element-13">
      {/* Sidebar */}
      <div className="w-64 bg-teal-800 text-white flex flex-col" data-id="element-14">
        <div className="p-4 flex items-center space-x-3 bg-teal-900" data-id="element-15">
          <img src="https://cdn.magicpatterns.com/uploads/d6s42Hs2WEUAvLVPoFhjsD/Copy_of_Clean_Elegant_Transparent_Logo_(3).png" alt="BMED Medical Centre Logo" className="h-10 w-10 object-contain rounded-full" data-id="element-16" />
          <div data-id="element-17">
            <h1 className="font-bold text-lg leading-tight" data-id="element-18">BMED CARE</h1>
            <p className="text-teal-300 text-xs" data-id="element-19">Medical Centre</p>
          </div>
        </div>

        <div className="p-4 border-b border-teal-700" data-id="element-20">
          <p className="text-sm font-medium" data-id="element-21">{user.name}</p>
          <p className="text-xs text-teal-300" data-id="element-22">{user.role}</p>
          <div className="flex items-center mt-2" data-id="element-23">
            <span className={`h-2 w-2 rounded-full mr-2 ${isOnline ? 'bg-green-400' : 'bg-yellow-400'}`} data-id="element-24" />
            <span className="text-xs text-teal-400" data-id="element-25">
              {isOnline ? 'Cloud Sync' : 'Local Mode'}
            </span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-id="element-26">
          {getNavItems().map(item => <a key={item.name} href="#" className="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors" data-id="element-27">
              <item.icon className="h-5 w-5 text-teal-300" data-id="element-28" />
              <span data-id="element-29">{item.name}</span>
            </a>)}
        </nav>

        <div className="p-4 border-t border-teal-700" data-id="element-30">
          <button onClick={logout} className="flex items-center space-x-3 px-3 py-2 w-full rounded-md text-sm font-medium hover:bg-teal-700 transition-colors text-red-200 hover:text-red-100" data-id="element-31">
            <LogOutIcon className="h-5 w-5" data-id="element-32" />
            <span data-id="element-33">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-34">
        {/* Top Header */}
        <header className="bg-white shadow-sm z-10" data-id="element-35">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-36">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-37">
              {user.role} Dashboard
            </h2>

            <div className="flex items-center space-x-4 relative" data-id="element-38">
              <button className="p-2 text-gray-400 hover:text-gray-500 relative" onClick={() => setShowNotifications(!showNotifications)} data-id="element-39">
                <BellIcon className="h-6 w-6" data-id="element-40" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" data-id="element-41" />}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && <div className="absolute right-0 top-10 mt-2 w-80 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-50" data-id="element-42">
                  <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center" data-id="element-43">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-44">
                      Notifications
                    </h3>
                    <span className="text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full" data-id="element-45">
                      {unreadCount} new
                    </span>
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-id="element-46">
                    {userNotifications.length === 0 ? <div className="px-4 py-3 text-sm text-gray-500 text-center" data-id="element-47">
                        No notifications
                      </div> : userNotifications.map(notif => <div key={notif.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 ${!notif.read ? 'bg-teal-50/30' : ''}`} onClick={() => markNotificationRead(notif.id)} data-id="element-48">
                          <p className={`text-sm ${!notif.read ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-49">
                            {notif.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-50">
                            {new Date(notif.date).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                          </p>
                        </div>)}
                  </div>
                </div>}
            </div>
          </div>
        </header>

        {/* Main scrollable area */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6 lg:p-8" data-id="element-51">
          {children}
        </main>
      </div>
    </div>;
};